import { Info as InfoIcon, Sync as SyncIcon } from "@mui/icons-material";
import {
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import MarketGrowthAdminApi from "../../../../../infrastructure/api/marketGrowthAdmin";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import FraudAlertDetailDialog from "../components/FraudAlertDetailDialog";
import MarketGrowthDataTable, {
  MarketGrowthServerPagination,
  TABLE_ACTION_ICON_SX,
} from "../components/MarketGrowthDataTable";
import MarketGrowthPageShell from "../components/MarketGrowthPageShell";
import { toSelectOptions } from "../constants/commissionEnums";
import {
  FRAUD_ALERT_SEVERITY_LABELS,
  getFraudAlertSeverity,
  getFraudAlertSeverityLabel,
  getFraudAlertStatusLabel,
  getFraudAlertTypeLabel,
  PARTNER_FRAUD_ALERT_STATUS_LABELS,
  PARTNER_FRAUD_ALERT_TYPE_LABELS,
} from "../constants/operationalEnums";
import { formatApiDate } from "../utils/commissionValidation";

const PAGE_SIZE = 20;

const formatDate = (value) =>
  value ? new Date(value).toLocaleString("fa-IR") : "—";

const getReviewedAtLabel = (row) => {
  const status = String(row?.status || "");
  if (status === "Open") return "—";
  return formatDate(row?.updateOn || row?.detectedAt);
};

const MarketGrowthFraudAlertsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [partners, setPartners] = useState([]);
  const [filters, setFilters] = useState({
    partnerId: "",
    alertType: "",
    severity: "",
    status: "",
    fromDate: null,
    toDate: null,
  });
  const [detailRow, setDetailRow] = useState(null);

  const loadPartners = useCallback(async () => {
    const res = await MarketGrowthAdminApi.getPartners();
    const normalized = MarketGrowthAdminApi.normalizeList(
      res,
      "بارگذاری نمایندگان ناموفق بود."
    );
    if (!normalized.error) {
      setPartners(Array.isArray(normalized.data) ? normalized.data : []);
    }
  }, []);

  const buildApiParams = useCallback(() => {
    const params = { page, pageSize: PAGE_SIZE };
    if (filters.alertType) params.alertType = filters.alertType;
    if (filters.status) params.status = filters.status;
    return params;
  }, [filters.alertType, filters.status, page]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await MarketGrowthAdminApi.getFraudAlerts(buildApiParams());
      const { error: err, data } = MarketGrowthAdminApi.normalize(
        res,
        "بارگذاری هشدارهای ضدتقلب ناموفق بود."
      );
      if (err) {
        setError(err);
        setItems([]);
        setTotalCount(0);
        return;
      }
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotalCount(Number(data?.totalCount) || 0);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [buildApiParams]);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  useEffect(() => {
    load();
  }, [load]);

  const partnerLabelMap = useMemo(
    () =>
      partners.reduce((acc, p) => {
        acc[p.id] = `کاربر ${p.userId} (${p.referralCode || p.id})`;
        return acc;
      }, {}),
    [partners]
  );

  const filteredItems = useMemo(() => {
    let rows = items;
    if (filters.partnerId) {
      const partnerId = Number(filters.partnerId);
      rows = rows.filter((row) => Number(row.partnerId) === partnerId);
    }
    if (filters.severity) {
      rows = rows.filter((row) => getFraudAlertSeverity(row.alertType) === filters.severity);
    }
    if (filters.fromDate) {
      const from = new Date(formatApiDate(filters.fromDate));
      rows = rows.filter((row) => row.detectedAt && new Date(row.detectedAt) >= from);
    }
    if (filters.toDate) {
      const to = new Date(formatApiDate(filters.toDate));
      to.setHours(23, 59, 59, 999);
      rows = rows.filter((row) => row.detectedAt && new Date(row.detectedAt) <= to);
    }
    return rows;
  }, [filters.fromDate, filters.partnerId, filters.severity, filters.toDate, items]);

  const hasClientSideFilters = Boolean(
    filters.partnerId || filters.severity || filters.fromDate || filters.toDate
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const applyFilters = () => {
    if (page !== 1) setPage(1);
    else load();
  };

  const resolvePartnerLabel = (row) =>
    row.partnerId ? partnerLabelMap[row.partnerId] || row.partnerId : "—";

  const tableRows = useMemo(
    () =>
      filteredItems.map((row) => ({
        ...row,
        alertTypeLabel: getFraudAlertTypeLabel(row.alertType),
        severityLabel: getFraudAlertSeverityLabel(row.alertType),
        statusLabel: getFraudAlertStatusLabel(row.status),
        partnerLabel: resolvePartnerLabel(row),
        reviewedAtLabel: getReviewedAtLabel(row),
      })),
    [filteredItems, partnerLabelMap]
  );

  const headCells = useMemo(
    () => [
      { id: "alertTypeLabel", label: "نوع هشدار" },
      { id: "severityLabel", label: "شدت", type: "chip" },
      { id: "statusLabel", label: "وضعیت", type: "chip" },
      { id: "partnerLabel", label: "نماینده" },
      { id: "description", label: "توضیحات" },
      { id: "detectedAt", label: "تاریخ ایجاد", type: "persiandatetime" },
      { id: "reviewedAtLabel", label: "تاریخ رسیدگی" },
    ],
    []
  );

  const tableActions = useMemo(
    () => [
      {
        icon: <InfoIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setDetailRow(row),
        color: "primary",
        tooltip: "جزئیات",
      },
    ],
    []
  );

  return (
    <MarketGrowthPageShell
      title="هشدارهای ضدتقلب"
      description="هشدارهای مشکوک در ثبت‌نام، نسبت‌دهی و تسویه نمایندگان."
      loading={loading}
      error={error}
      onRetry={load}
    >
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>نماینده</InputLabel>
                <Select
                  label="نماینده"
                  value={filters.partnerId}
                  onChange={(e) => setFilters((f) => ({ ...f, partnerId: e.target.value }))}
                >
                  <MenuItem value="">همه</MenuItem>
                  {partners.map((p) => (
                    <MenuItem key={p.id} value={String(p.id)}>
                      {partnerLabelMap[p.id]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>نوع هشدار</InputLabel>
                <Select
                  label="نوع هشدار"
                  value={filters.alertType}
                  onChange={(e) => setFilters((f) => ({ ...f, alertType: e.target.value }))}
                >
                  <MenuItem value="">همه</MenuItem>
                  {toSelectOptions(PARTNER_FRAUD_ALERT_TYPE_LABELS).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>شدت</InputLabel>
                <Select
                  label="شدت"
                  value={filters.severity}
                  onChange={(e) => setFilters((f) => ({ ...f, severity: e.target.value }))}
                >
                  <MenuItem value="">همه</MenuItem>
                  {toSelectOptions(FRAUD_ALERT_SEVERITY_LABELS).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>وضعیت</InputLabel>
                <Select
                  label="وضعیت"
                  value={filters.status}
                  onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                >
                  <MenuItem value="">همه</MenuItem>
                  {toSelectOptions(PARTNER_FRAUD_ALERT_STATUS_LABELS).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <JalaliDatePicker
                label="از تاریخ"
                value={filters.fromDate}
                onChange={(value) => setFilters((f) => ({ ...f, fromDate: value }))}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <JalaliDatePicker
                label="تا تاریخ"
                value={filters.toDate}
                onChange={(value) => setFilters((f) => ({ ...f, toDate: value }))}
              />
            </Grid>
            <Grid item xs={12} md="auto">
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={applyFilters}>
                  اعمال فیلتر
                </Button>
                <Button variant="outlined" startIcon={<SyncIcon />} onClick={load}>
                  بروزرسانی
                </Button>
              </Stack>
            </Grid>
          </Grid>
          {hasClientSideFilters ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
              فیلترهای نماینده، شدت و تاریخ روی نتایج صفحه جاری اعمال می‌شوند.
            </Typography>
          ) : null}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <MarketGrowthDataTable
            rowKey="id"
            rows={tableRows}
            headCells={headCells}
            actions={tableActions}
            pageSize={PAGE_SIZE}
            showPaging={false}
            defaultSort={{ orderBy: "detectedAt", order: "desc" }}
            tableMinWidth={1000}
          />
          <MarketGrowthServerPagination
            page={page}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </CardContent>
      </Card>

      <FraudAlertDetailDialog
        open={Boolean(detailRow)}
        onClose={() => setDetailRow(null)}
        data={detailRow}
        partnerLabel={detailRow ? resolvePartnerLabel(detailRow) : null}
      />
    </MarketGrowthPageShell>
  );
};

export default MarketGrowthFraudAlertsPage;
